import type { FC, HTMLAttributes, ReactNode } from "react";

import { Text } from "./Text";

export interface ListItemCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Children content
   */
  children?: ReactNode;

  /**
   * Whether the card is selected/active
   */
  selected?: boolean;

  /**
   * Whether the card is clickable
   */
  clickable?: boolean;

  /**
   * Optional header content (left side)
   */
  header?: ReactNode;

  /**
   * Optional actions content (right side)
   */
  actions?: ReactNode;

  /**
   * Optional footer content
   */
  footer?: ReactNode;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Padding variant
   */
  padding?: "sm" | "md" | "lg";
}

/**
 * ListItemCard Component
 *
 * A reusable card component for list items with consistent styling.
 *
 * @example
 * ```tsx
 * <ListItemCard
 *   header={<div>Item Title</div>}
 *   actions={<Button size="sm">Delete</Button>}
 *   footer={<span>Updated 2 hours ago</span>}
 * >
 *   Main content here
 * </ListItemCard>
 * ```
 */
export const ListItemCard: FC<ListItemCardProps> = ({
  children,
  selected = false,
  clickable = false,
  header,
  actions,
  footer,
  className = "",
  padding = "md",
  onClick,
  ...props
}) => {
  const paddingClasses = { lg: "p-4", md: "p-3", sm: "p-2" };

  const baseClasses = `
    rounded-none border text-sm
    ${
      selected
        ? "border-emerald-500/40 bg-emerald-500/10 text-gray-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-white"
        : "border-slate-300/70 bg-transparent text-gray-600 dark:border-slate-700 dark:text-white/70"
    }
    ${paddingClasses[padding]}
    ${
      clickable && !selected
        ? "cursor-pointer transition-colors hover:border-slate-400 hover:bg-slate-100/80 dark:hover:border-slate-600 dark:hover:bg-white/5"
        : ""
    }
    ${className}
  `
    .trim()
    .replace(/\s+/g, " ");

  return (
    <div
      className={baseClasses}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if ((e.key === "Enter" || e.key === " ") && onClick) {
                e.preventDefault();
                e.currentTarget.click();
              }
            }
          : undefined
      }
      {...props}
    >
      {(header ?? actions) && (
        <div className="flex items-center justify-between gap-3">
          {header && <div className="min-w-0 flex-1">{header}</div>}
          {actions && (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          )}
        </div>
      )}

      {children && (
        <div className={header || actions ? "mt-2" : ""}>{children}</div>
      )}

      {footer && (
        <Text as="div" className="mt-2">
          {footer}
        </Text>
      )}
    </div>
  );
};

/**
 * ListItemCardHeader - Header component for ListItemCard
 */
export const ListItemCardHeader: FC<{
  title: ReactNode;
  subtitle?: ReactNode;
  badge?: ReactNode;
}> = ({ title, subtitle, badge }) => (
  <div>
    <div className="flex items-center gap-2">
      <div className="font-mono text-[11px] font-bold text-slate-900 dark:text-white">
        {title}
      </div>
      {badge}
    </div>
    {subtitle && <Text as="div">{subtitle}</Text>}
  </div>
);

/**
 * ListItemCardMetadata - Metadata display component
 */
export const ListItemCardMetadata: FC<{
  data: Record<string, unknown>;
  className?: string;
}> = ({ data, className = "" }) => (
  <pre
    className={`overflow-x-auto whitespace-pre-wrap break-all rounded-none border border-slate-300/80 bg-slate-50/80 p-2 text-xs text-gray-600 dark:border-slate-700 dark:bg-arti-background dark:text-white/60 ${className}`}
  >
    {JSON.stringify(data, null, 2)}
  </pre>
);
